import type { Article, Locale, ProductCategory } from '@/types';
import { ARTICLE_SLUGS, type EDIT_CATEGORIES } from '@/content/articles';
import { MADRID_TIME_ZONE, madridOffset } from '@/lib/madridTime';
import type { GameId } from '@/lib/colorimetry/landing';
import { GUIDE_PATH } from '@/lib/colorimetry/route';

/** Slug of an Edit category (estilo, belleza, bienestar, casa, mujer-40). */
type EditCategorySlug = (typeof EDIT_CATEGORIES)[number]['slug'];

/** CARO publishes for Spain first: dates without a time mean Madrid midnight. */
export const EDIT_TIME_ZONE = MADRID_TIME_ZONE;

/**
 * The instant an Edit article goes public, in ms since the epoch: its
 * `publishAt` (ISO with offset) or, without one, its `date` at 00:00 Madrid
 * time (Spain changes clocks at 02:00/03:00, so midnight is never ambiguous).
 * An unparseable value yields NaN, which `isPublished` treats as never.
 */
export function publishInstant(a: Pick<Article, 'date' | 'publishAt'>): number {
  if (a.publishAt) return Date.parse(a.publishAt);
  const utcMidnight = Date.parse(`${a.date}T00:00:00Z`);
  if (!Number.isFinite(utcMidnight)) return NaN;
  return utcMidnight - madridOffset(utcMidnight);
}

/** Whether the article is public at `now` (ms). Scheduled ones are not. */
export function isPublished(a: Pick<Article, 'date' | 'publishAt'>, now: number): boolean {
  const at = publishInstant(a);
  return Number.isFinite(at) && at <= now;
}

/** The articles public at `now`, most recently published first. */
export function publishedArticles<T extends Pick<Article, 'date' | 'publishAt'>>(list: T[], now: number): T[] {
  return list.filter((a) => isPublished(a, now)).sort((x, y) => publishInstant(y) - publishInstant(x));
}

// --- Per-locale slugs (ARTICLE_SLUGS) -----------------------------------------

/** Public slug of the article `esSlug` in `locale`; the Spanish slug for es, or for an article without a row. */
export function articleSlugFor(locale: Locale, esSlug: string): string {
  if (locale === 'es' || !Object.hasOwn(ARTICLE_SLUGS, esSlug)) return esSlug;
  return ARTICLE_SLUGS[esSlug]![locale];
}

/**
 * Spanish (logical) slug of a public `slug` in `locale`. The Spanish slug itself
 * is accepted too (old EN/FR URLs); an unknown slug comes back unchanged.
 */
export function articleEsSlug(locale: Locale, slug: string): string {
  if (locale === 'es') return slug;
  for (const [es, bySlug] of Object.entries(ARTICLE_SLUGS)) {
    if (bySlug[locale] === slug) return es;
  }
  return slug;
}

/** Spanish slug of a slug in ANY locale (API lookups); unknown → unchanged. */
export function articleEsSlugAnyLocale(slug: string): string {
  for (const locale of ['en', 'fr'] as const) {
    const es = articleEsSlug(locale, slug);
    if (es !== slug) return es;
  }
  return slug;
}

/**
 * Edit categories that map to a subject store, with the CTA's dictionary key.
 * Others (bienestar, mujer-40) have no shop, so no CTA is shown for them.
 * Shared by ArticleView, TheEditPage and the Worker's prerendered articleHtml.
 */
export const EDIT_CATEGORY_SHOP: Record<string, { href: string; key: string }> = {
  estilo: { href: '/shop/estilo', key: 'edit.shopEstilo' },
  belleza: { href: '/shop/beauty', key: 'edit.shopBeauty' },
  casa: { href: '/shop/casa', key: 'edit.shopCasa' },
};

/** The image-consulting button of an article with `endLinks: ['services']` (client and prerender). */
export const EDIT_SERVICES_CTA = { href: '/caro/servicios', key: 'edit.services' } as const;

/** The guide «Combinar colores» button of an article with `endLinks: ['guide']` (client and prerender). */
export const EDIT_GUIDE_CTA = { href: GUIDE_PATH, key: 'edit.guide' } as const;

/** The style-test blocks an article closes with (`endLinks`), in GAME_IDS order (client and prerender; not imported: landing.json stays out of the bundle). */
export function articlePromoGames(a: Pick<Article, 'endLinks'>): GameId[] {
  return (['colorimetry', 'silhouette', 'face'] as const).filter((g) => a.endLinks?.includes(g));
}

/**
 * Each shop category's Edit category (the inverse of EDIT_CATEGORY_SHOP:
 * beauty→belleza, estilo→estilo, casa→casa).
 */
export const SHOP_EDIT_CATEGORY: Record<ProductCategory, EditCategorySlug> = {
  beauty: 'belleza',
  estilo: 'estilo',
  casa: 'casa',
};

/** Max articles in a shop category's «En el Edit» block. */
export const SHOP_EDIT_ARTICLES_MAX = 3;

/**
 * A shop category's «En el Edit» block: the articles of its Edit category
 * public at `now` (scheduled ones never), most recent first, at most
 * SHOP_EDIT_ARTICLES_MAX. The one selection used by ShopPage (client) and the
 * Worker's prerendered categoryHtml, so both list the same articles.
 */
export function shopCategoryArticles<T extends Pick<Article, 'date' | 'publishAt' | 'category'>>(
  list: T[],
  shopCategory: ProductCategory,
  now: number,
): T[] {
  const editCategory = SHOP_EDIT_CATEGORY[shopCategory];
  return publishedArticles(
    list.filter((a) => a.category === editCategory),
    now,
  ).slice(0, SHOP_EDIT_ARTICLES_MAX);
}
